/**
 * Editor - one WYSIWYG instance over its hidden <textarea>: the
 * engine-independent SHELL. The engine (engine-quill.js / engine-tiptap.js)
 * owns the visual editor; the shell owns the FIELD CONTRACT, identical for
 * every flavor.
 *
 * DOM contract (the public API, keep stable):
 *   <div class="gedit gedit-<flavor>">   <- wrapper; genus-invalid / gedit-disabled land here
 *     <textarea ...>                     <- the value holder (hidden): name, validate-*, HTML value
 *     <div class="gedit-area">           <- the engine mounts here (toolbar + content)
 *   </div>
 *
 * Sync rules:
 *   - textarea.value is CLEAN HTML, '' while the content is really empty -
 *     so `required` works everywhere;
 *   - every USER edit dispatches native `input` on the textarea (bubbles);
 *   - losing focus dispatches native `change` when the value differs from the
 *     one at focus time - the definitive value-change event, like a plain
 *     textarea (validator listens to both, former's dirty tracking too);
 *   - programmatic setValue() fires nothing (change events only on REAL user
 *     changes; former snapshots after fill by itself).
 *
 * The link flow is shared: g.prompt (dialog lib, translated) with bare
 * domains auto-schemed to https; the engine only snapshots/applies.
 * Disabled/readonly follow the textarea's own attributes (+ any ancestor
 * fieldset's disabled - the conditional-section house pattern).
 */

const t = (text) => window.g.translate(text);

export class Editor {
	constructor(el, opts, EngineClass) {
		this.el = el;
		el.editor = this;
		this.opts = opts;

		const wrap = document.createElement('div');
		wrap.className = 'gedit gedit-' + opts.flavor;
		el.parentNode.insertBefore(wrap, el);
		wrap.appendChild(el);
		const area = document.createElement('div');
		area.className = 'gedit-area';
		wrap.appendChild(area);
		this.wrap = wrap;

		this.engine = new EngineClass(area, {
			...opts,
			placeholder: opts.placeholder || el.getAttribute('placeholder') || '',
		}, this);
		// per-flavor escape hatches (documented): instance.quill / instance.tiptap
		if (this.engine.quill) this.quill = this.engine.quill;
		if (this.engine.editor) this.tiptap = this.engine.editor;

		// initial content: the textarea's authored default / server-rendered
		// value; normalize the holder right away so the round-trip is stable
		if (el.value) this.engine.setHTML(el.value);
		el.value = this.html();
		this.focusValue = el.value;

		// native form reset restores the textarea's DEFAULT value - follow it
		this.onReset = () => setTimeout(() => this.setValue(this.el.value), 0);
		if (el.form) el.form.addEventListener('reset', this.onReset);

		this.observer = new MutationObserver(() => this.updateDisabled());
		this.observer.observe(el, { attributes: true, attributeFilter: ['disabled', 'readonly'] });
		for (let node = el.parentElement; node; node = node.parentElement) {
			if (node.tagName === 'FIELDSET') this.observer.observe(node, { attributes: true, attributeFilter: ['disabled'] });
		}
		this.updateDisabled();
	}

	// --- engine hooks --------------------------------------------------------

	onUserEdit() {
		this.el.value = this.html();
		this.el.dispatchEvent(new Event('input', { bubbles: true }));
	}

	onFocus() {
		this.focusValue = this.el.value;
	}

	onBlur() {
		if (this.el.value !== this.focusValue) {
			this.focusValue = this.el.value;
			this.el.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	// --- value ---------------------------------------------------------------

	// The clean HTML for the holder ('' when empty).
	html() {
		return this.engine.isEmpty() ? '' : this.engine.html();
	}

	// The plain text (what validation rules run on).
	text() {
		return this.engine.isEmpty() ? '' : this.engine.text();
	}

	getValue() {
		return this.el.value;
	}

	// Programmatic write (former's fill path) - no input/change events.
	setValue(html) {
		this.engine.setHTML(html || '');
		this.el.value = this.html();
		this.focusValue = this.el.value;
	}

	focus() {
		this.engine.focus();
	}

	// --- link flow (shared: g.prompt instead of engine tooltips) -------------

	async linkPrompt() {
		// one prompt at a time: a double click on the toolbar's Link button
		// opened two (the flag is the guard — the prompt opts out of the
		// g.action detector)
		if (this.linking) return;
		this.linking = true;
		try { await this.askLink(); } finally { this.linking = false; }
	}

	async askLink() {
		const state = this.engine.linkState();
		const url = await (window.g.prompt
			? window.g.prompt(t('Link address'), {
				value: state.href || '',
				placeholder: 'https://',
				unguarded: true,
				validate: (v) => {
					const val = v.trim();
					if (!val || /^(https?:\/\/|mailto:|\/)/i.test(val)) return null;
					if (/^[\w-]+(\.[\w-]+)+/.test(val)) return null; // bare domain - schemed below
					return t('Enter a web address (https://...), a mailto: address or a path starting with /.');
				},
			})
			: Promise.resolve(window.prompt(t('Link address'), state.href || '')));
		if (url === null) return;
		let val = url.trim();
		if (val && !/^(https?:\/\/|mailto:|\/)/i.test(val)) val = 'https://' + val;
		this.engine.applyLink(val || null, state);
	}

	// --- state ---------------------------------------------------------------

	updateDisabled() {
		const disabled = this.el.matches(':disabled');
		const readonly = this.el.hasAttribute('readonly');
		this.engine.enable(!disabled && !readonly);
		this.wrap.classList.toggle('gedit-disabled', disabled);
		this.wrap.classList.toggle('gedit-readonly', readonly && !disabled);
	}

	// Tear down: restore the plain textarea (its value keeps the HTML).
	destroy() {
		this.observer.disconnect();
		if (this.el.form) this.el.form.removeEventListener('reset', this.onReset);
		this.engine.destroy();
		this.wrap.parentNode.insertBefore(this.el, this.wrap);
		this.wrap.remove();
		delete this.el.editor;
	}
}
